import 'url-polyfill';
import { DebugLog } from './debug-log';

export { getLocale } from './translator';
export { logMessage } from './sentry';
export { updateQueryStringParameters } from './url-utils';

export function once<T extends (...args: any[]) => any>(
  fn: T | null,
  context?: ThisParameterType<T>
): (...args: Parameters<T>) => ReturnType<T> | undefined {
  let result: ReturnType<T> | undefined;
  let func: T | null = fn;
  return function(
    this: any,
    ...args: Parameters<T>
  ): ReturnType<T> | undefined {
    if (func) {
      result = func.apply(context || this, args);
      func = null;
    }
    return result;
  };
}

let lastSharedFunctionExecutionMS: number = 0;
const singleCallTimerShared: number = 2000;

export function preventSpamCallsShared<T extends (...args: any[]) => any>(
  fn: T
): (...args: Parameters<T>) => ReturnType<T> | undefined {
  return (...args: Parameters<T>): ReturnType<T> | undefined => {
    if (Date.now() - lastSharedFunctionExecutionMS > singleCallTimerShared) {
      lastSharedFunctionExecutionMS = Date.now();
      return fn(...args);
    } else {
      DebugLog.log('call to fast to the function, wait', {
        delay:
          singleCallTimerShared - (Date.now() - lastSharedFunctionExecutionMS)
      });
      return undefined;
    }
  };
}

export function preventSpamCalls<T extends () => any>(
  fn: T
): () => ReturnType<T> | undefined {
  let lastFunctionExecutionMS: number = 0;
  const singleCallTimer: number = 2000;
  return (): ReturnType<T> | undefined => {
    if (Date.now() - lastFunctionExecutionMS > singleCallTimer) {
      lastFunctionExecutionMS = Date.now();
      return fn();
    } else {
      DebugLog.log('call to fast to the function, wait', {
        delay: singleCallTimer - (Date.now() - lastFunctionExecutionMS)
      });
      return undefined;
    }
  };
}

export function deepEqual(
  x: Record<string, any>,
  y: Record<string, any>
): boolean {
  const ok = Object.keys;
  const tx = typeof x;
  const ty = typeof y;
  return x && y && tx === 'object' && tx === ty
    ? ok(x).length === ok(y).length &&
        ok(x).every(key => deepEqual(x[key], y[key]))
    : x === y;
}

interface CustomEventOptions {
  bubbles?: boolean;
  cancelable?: boolean;
  detail?: any;
}

export function triggerEvent(
  type: string,
  options: CustomEventOptions = {
    bubbles: false,
    cancelable: false,
    detail: undefined
  }
): void {
  let newEvent: CustomEvent;

  if (!(window as any).CustomEvent) {
    newEvent = document.createEvent('CustomEvent') as CustomEvent;
    (newEvent as any).initCustomEvent(
      type,
      options.bubbles,
      options.cancelable,
      options.detail
    );
  } else {
    newEvent = new CustomEvent(type, options);
  }

  window.dispatchEvent(newEvent);
}

export function triggerEventInBrowser(
  type: string,
  options: CustomEventOptions = {
    bubbles: false,
    cancelable: false,
    detail: undefined
  },
  targetFrameName: string = ''
): void {
  let newEvent: CustomEvent;

  if (!(window as any).CustomEvent) {
    newEvent = document.createEvent('CustomEvent') as CustomEvent;
    (newEvent as any).initCustomEvent(
      type,
      options.bubbles,
      options.cancelable,
      options.detail
    );
  } else {
    newEvent = new CustomEvent(type, options);
  }

  function doTrigger(scope: Window): void {
    const frames = scope.frames;

    for (let i = 0; i < frames.length; i++) {
      try {
        if (
          !targetFrameName ||
          (targetFrameName && (frames[i] as any).name === targetFrameName)
        ) {
          frames[i].dispatchEvent(newEvent);
        }
      } catch (e) {
        // In case we access a cross-origin frame
      }
    }

    if (scope.parent !== scope) {
      doTrigger(scope.parent as Window);
    }
  }

  doTrigger(window);
}

export function polyfillArrayFind(): void {
  if (!Array.prototype.find) {
    const method = 'find';
    Object.defineProperty(Array.prototype, method, {
      value: function<T>(
        predicate: (value: T, index: number, obj: T[]) => boolean
      ): T | undefined {
        if (this == null) {
          throw new TypeError(
            'Array.prototype.find called on null or undefined'
          );
        }
        if (typeof predicate !== 'function') {
          throw new TypeError('predicate must be a function');
        }
        const list = Object(this);
        const length = list.length >>> 0;
        const thisArg = arguments[1];
        let value: T;

        for (let i = 0; i < length; i++) {
          value = list[i];
          if (predicate.call(thisArg, value, i, list)) {
            return value;
          }
        }
        return undefined;
      }
    });
  }
}

export function postMessageBrowser(message: string, at: string = '*'): void {
  function doPost(scope: Window): void {
    try {
      scope.postMessage(message, at);
    } catch (e) {
      // In case we access a cross-origin frame
    }

    if (scope.parent !== scope) {
      doPost(scope.parent as Window);
    }
  }

  doPost(window);
}
